/**
 * Pilot feedback on a case: "this was right / wrong", with an optional comment, on the
 * customer or on one line (item, quantity, price).
 *
 * Stored in the app database only and visible to the managers of the area; a rep reads
 * back their own rows. Append only: the newest row of an author for a line and topic
 * counts.
 *
 * Kept free of React so the request shapes and the error wording can be tested.
 */

import { type ApiError, client } from "./client";
import type { CaseFeedback, CaseFeedbackRequest, FeedbackTopic } from "./live-types";
import { detailText } from "./work-actions";

/** Longest comment the server accepts. */
export const FEEDBACK_COMMENT_MAX = 500;

/** Shown under the comment field. */
export const FEEDBACK_HINT = "No customer names or prices in the comment.";

export const feedbackKey = (caseId: string) => ["case-feedback", caseId];

/** The topics a line can be judged on, with their labels. */
export const LINE_TOPICS: readonly { value: FeedbackTopic; label: string }[] = [
  { value: "item", label: "Item" },
  { value: "quantity", label: "Quantity" },
  { value: "price", label: "Price" },
];

/** The request body: a trimmed comment, left out when empty. Throws on a comment that is too long. */
export function feedbackBody(request: CaseFeedbackRequest): CaseFeedbackRequest {
  const comment = request.comment?.trim() ?? "";
  if (comment.length > FEEDBACK_COMMENT_MAX) {
    throw new RangeError(`The comment is longer than ${FEEDBACK_COMMENT_MAX} characters.`);
  }
  return {
    line_index: request.line_index,
    topic: request.topic,
    verdict: request.verdict,
    ...(comment === "" ? {} : { comment }),
  };
}

/** Record one verdict. */
export async function postCaseFeedback(caseId: string, request: CaseFeedbackRequest) {
  const { data } = await client.post<{ 201: CaseFeedback }, unknown, true>({
    url: "/cases/{case_id}/feedback",
    path: { case_id: caseId },
    body: feedbackBody(request),
    headers: { "Content-Type": "application/json" },
    throwOnError: true,
  });
  return data;
}

function isFeedback(raw: unknown): raw is CaseFeedback {
  const row = raw as CaseFeedback | null;
  return (
    row != null &&
    typeof row === "object" &&
    typeof row.topic === "string" &&
    (row.verdict === "right" || row.verdict === "wrong") &&
    typeof row.author === "string"
  );
}

/** The rows the caller may see. A list or `{items: [...]}`; an older backend (404) has none. */
export async function listCaseFeedback(caseId: string): Promise<CaseFeedback[]> {
  try {
    const { data } = await client.get<
      { 200: CaseFeedback[] | { items: CaseFeedback[] } },
      unknown,
      true
    >({
      url: "/cases/{case_id}/feedback",
      path: { case_id: caseId },
      throwOnError: true,
    });
    const rows: unknown = Array.isArray(data)
      ? data
      : (data as { items?: unknown } | undefined)?.items;
    return Array.isArray(rows) ? rows.filter(isFeedback) : [];
  } catch (error) {
    if ((error as ApiError | null)?.status === 404) {
      return [];
    }
    throw error;
  }
}

/**
 * The newest verdict of `author` on the customer (`lineIndex` null) or on one line,
 * for one topic or any topic.
 */
export function latestFeedback(
  rows: readonly CaseFeedback[],
  author: string | null | undefined,
  lineIndex: number | null,
  topic?: FeedbackTopic,
): CaseFeedback | null {
  const me = (author ?? "").trim().toLowerCase();
  if (me === "") {
    return null;
  }
  let latest: CaseFeedback | null = null;
  for (const row of rows) {
    if (row.author.trim().toLowerCase() !== me) {
      continue;
    }
    if ((row.line_index ?? null) !== lineIndex) {
      continue;
    }
    if (topic && row.topic !== topic) {
      continue;
    }
    if (!latest || Date.parse(row.at) >= Date.parse(latest.at)) {
      latest = row;
    }
  }
  return latest;
}

/** What a failed feedback write reads as. */
export function feedbackFailureText(error: unknown): string {
  if (error instanceof RangeError) {
    return error.message;
  }
  const { status, body } = (error ?? {}) as Partial<ApiError>;
  if (status === 422) {
    return detailText(body) ?? "The server did not accept this.";
  }
  if (status === 403) {
    return "Your role does not allow this.";
  }
  return "This could not be saved. Try again in a moment.";
}
